import { useEffect, useState } from "react";
import { api, formatApiError, fmtCurrency } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { MoneyInput } from "@/components/MoneyInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

/**
 * ExistingLoansPanel — beheer van bestaande leningen per club.
 *
 * Elke lening krijgt principal, rente, looptijd (jaren), exacte startdatum en
 * type (annuïteit of lineair). Het scenario-compute-endpoint amortiseert deze
 * leningen maand voor maand en projecteert per jaar de rente + aflossing —
 * zonder dat de gebruiker een statische rente uit het laatste boekjaar hoeft
 * te onderhouden.
 */
export default function ExistingLoansPanel({ clubId, locked = false, onChanged }) {
  const t = useT();
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);          // {id?, name, principal, ...}
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/clubs/${clubId}/existing-loans`)
      .then(({ data }) => setLoans(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || String(e.message || e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clubId]);

  const openNew = () => {
    setDraft({
      name: "",
      principal: 100000,
      interest_rate: 0.04,
      term_years: 20,
      start_date: new Date().toISOString().slice(0, 10),
      loan_type: "annuity",
      notes: "",
    });
    setOpen(true);
  };
  const openEdit = (l) => { setDraft({ ...l }); setOpen(true); };
  const closeDialog = () => { setOpen(false); setDraft(null); };

  const submit = async () => {
    if (!draft) return;
    if (!draft.name?.trim()) return toast.error(t("Naam is verplicht"));
    if (!draft.start_date) return toast.error(t("Startdatum is verplicht"));
    if (!(draft.principal > 0)) return toast.error(t("Hoofdsom moet groter dan 0 zijn"));
    if (!(draft.term_years > 0)) return toast.error(t("Looptijd moet ≥ 1 jaar zijn"));
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        principal: parseFloat(draft.principal),
        interest_rate: parseFloat(draft.interest_rate),
        term_years: parseInt(draft.term_years, 10),
        start_date: draft.start_date,
        loan_type: draft.loan_type || "annuity",
        notes: draft.notes || "",
      };
      if (draft.id) {
        await api.put(`/existing-loans/${draft.id}`, payload);
        toast.success(t("Lening bijgewerkt"));
      } else {
        await api.post(`/clubs/${clubId}/existing-loans`, payload);
        toast.success(t("Lening toegevoegd"));
      }
      closeDialog();
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (id) => {
    try {
      await api.delete(`/existing-loans/${id}`);
      toast.success(t("Verwijderd"));
      setConfirmDelete(null);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || String(e.message || e));
    }
  };

  // Simple client-side estimator so the dialog shows an approximate yearly
  // payment while the user tweaks the numbers. Server does the exact amortisation.
  const estimateAnnualPayment = () => {
    if (!draft) return 0;
    const P = Number(draft.principal) || 0;
    const r = Number(draft.interest_rate) || 0;
    const n = Number(draft.term_years) || 0;
    if (P <= 0 || n <= 0) return 0;
    if (draft.loan_type === "linear") {
      // First-year linear payment ≈ P/n + P*r
      return P / n + P * r;
    }
    // Annuity
    if (r > 0) return P * r / (1 - Math.pow(1 + r, -n));
    return P / n;
  };

  const totalPrincipal = loans.reduce((s, l) => s + (Number(l.principal) || 0), 0);

  return (
    <div className="space-y-4" data-testid="existing-loans-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display uppercase font-black text-2xl tracking-tight">{t("Bestaande leningen")}</div>
          <div className="text-sm text-muted-foreground max-w-2xl">
            {t("Voer per lopende lening de hoofdsom, rente, looptijd en exacte startdatum in. Het scenario berekent de exacte rente en aflossing per jaar (annuïteit of lineair) op basis daarvan — geen statische rente meer nodig.")}
          </div>
        </div>
        <button
          onClick={openNew}
          disabled={locked}
          data-testid="exloan-add-btn"
          className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" /> {t("Lening toevoegen")}
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">{t("Laden…")}</div>
      ) : loans.length === 0 ? (
        <div className="border-2 border-dashed border-foreground/40 p-10 text-center" data-testid="exloan-empty">
          <div className="font-display uppercase font-bold text-xl mt-2">{t("Nog geen bestaande leningen")}</div>
          <p className="text-muted-foreground max-w-lg mx-auto mt-2">
            {t("Voeg elke nog lopende lening toe (bijv. hypotheek clubhuis, banklening steiger). Zonder ingevoerde leningen valt het scenario terug op de statische rente uit het laatste boekjaar.")}
          </p>
        </div>
      ) : (
        <div className="border-2 border-foreground overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b-2 border-foreground">
              <tr>
                <th className="text-left px-3 py-2 stat-label">{t("Naam")}</th>
                <th className="text-right px-3 py-2 stat-label">{t("Hoofdsom")}</th>
                <th className="text-right px-3 py-2 stat-label">{t("Rente")}</th>
                <th className="text-right px-3 py-2 stat-label">{t("Looptijd")}</th>
                <th className="text-left px-3 py-2 stat-label">{t("Startdatum")}</th>
                <th className="text-left px-3 py-2 stat-label">{t("Type")}</th>
                <th className="text-right px-3 py-2 stat-label">{t("Acties")}</th>
              </tr>
            </thead>
            <tbody>
              {loans.map((l) => (
                <tr key={l.id} className="border-b border-border" data-testid={`exloan-row-${l.id}`}>
                  <td className="px-3 py-2 font-medium">{l.name}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{fmtCurrency(l.principal)}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{(Number(l.interest_rate) * 100).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</td>
                  <td className="px-3 py-2 text-right font-mono-num">{l.term_years} {t("jr")}</td>
                  <td className="px-3 py-2 font-mono-num">{l.start_date}</td>
                  <td className="px-3 py-2">{l.loan_type === "linear" ? t("Lineair") : t("Annuïteit")}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => openEdit(l)}
                      disabled={locked}
                      data-testid={`exloan-edit-${l.id}`}
                      className="mr-2 opacity-60 hover:opacity-100 hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed"
                      title={t("Bewerken")}
                    ><Pencil className="w-4 h-4 inline" /></button>
                    <button
                      onClick={() => setConfirmDelete(l)}
                      disabled={locked}
                      data-testid={`exloan-delete-${l.id}`}
                      className="opacity-60 hover:opacity-100 hover:text-destructive disabled:opacity-30 disabled:cursor-not-allowed"
                      title={t("Verwijderen")}
                    ><Trash2 className="w-4 h-4 inline" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-muted/30 border-t-2 border-foreground">
              <tr>
                <td className="px-3 py-2 font-bold stat-label">{t("Totaal hoofdsom")}</td>
                <td className="px-3 py-2 text-right font-mono-num font-bold" data-testid="exloan-total-principal">{fmtCurrency(totalPrincipal)}</td>
                <td colSpan={5} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => (v ? null : closeDialog())}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="pr-8">{draft?.id ? t("Lening bewerken") : t("Nieuwe bestaande lening")}</DialogTitle>
            <DialogDescription>
              {t("Voer de gegevens exact in zoals in de leningsovereenkomst. Het systeem amortiseert maand voor maand vanaf de startdatum.")}
            </DialogDescription>
          </DialogHeader>
          {draft && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="stat-label">{t("Naam")}</label>
                <input
                  type="text"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder={t("bv. Hypotheek clubhuis")}
                  data-testid="exloan-name"
                  className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="stat-label">{t("Hoofdsom (€)")}</label>
                <MoneyInput
                  value={draft.principal}
                  onCommit={(v) => setDraft({ ...draft, principal: v })}
                  testid="exloan-principal"
                />
              </div>
              <div>
                <label className="stat-label">{t("Rentepercentage")}</label>
                <div className="relative mt-1">
                  <input
                    type="number"
                    step="any"
                    value={draft.interest_rate === "" || draft.interest_rate == null
                      ? ""
                      : (Number(draft.interest_rate) * 100).toString().replace(/\.0+$/, "")}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || v == null) { setDraft({ ...draft, interest_rate: "" }); return; }
                      const n = Number(v.replace(",", "."));
                      if (Number.isNaN(n)) { setDraft({ ...draft, interest_rate: "" }); return; }
                      setDraft({ ...draft, interest_rate: Math.round((n / 100) * 1e6) / 1e6 });
                    }}
                    data-testid="exloan-rate"
                    className="w-full border-2 border-foreground bg-background px-3 py-2 pr-8 font-mono-num focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono-num pointer-events-none">%</span>
                </div>
              </div>
              <div>
                <label className="stat-label">{t("Looptijd (jaren)")}</label>
                <input
                  type="number"
                  step="1"
                  min="1"
                  value={draft.term_years}
                  onChange={(e) => setDraft({ ...draft, term_years: e.target.value })}
                  data-testid="exloan-term"
                  className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 font-mono-num focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="stat-label">{t("Startdatum")}</label>
                <input
                  type="date"
                  value={draft.start_date}
                  onChange={(e) => setDraft({ ...draft, start_date: e.target.value })}
                  data-testid="exloan-start-date"
                  className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 font-mono-num focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <div className="text-xs text-muted-foreground mt-1">{t("Dag dat de lening is afgesloten en de rente begon te lopen.")}</div>
              </div>
              <div>
                <label className="stat-label">{t("Aflossingsstructuur")}</label>
                <select
                  value={draft.loan_type}
                  onChange={(e) => setDraft({ ...draft, loan_type: e.target.value })}
                  data-testid="exloan-type"
                  className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="annuity">{t("Annuïteit (gelijke jaarlast)")}</option>
                  <option value="linear">{t("Lineair (gelijke aflossing)")}</option>
                </select>
              </div>

              <div className="md:col-span-2 border-2 border-foreground bg-muted/30 px-4 py-3">
                <div className="stat-label mb-1">{t("Geschatte eerste jaarlast")}</div>
                <div className="font-mono-num font-bold text-xl" data-testid="exloan-est-annual">
                  {fmtCurrency(estimateAnnualPayment())}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {t("Ter indicatie. De exacte maand-per-maand berekening (inclusief pro-rata Y0) verschijnt in het scenario.")}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="stat-label">{t("Notities (optioneel)")}</label>
                <textarea
                  rows={2}
                  value={draft.notes}
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                  data-testid="exloan-notes"
                  className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={closeDialog} className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold text-sm" data-testid="exloan-cancel">
              {t("Annuleren")}
            </button>
            <button
              onClick={submit}
              disabled={saving}
              data-testid="exloan-save"
              className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm disabled:opacity-40"
            >
              {saving ? t("Opslaan…") : t("Opslaan")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDelete} onOpenChange={(v) => (v ? null : setConfirmDelete(null))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("Lening verwijderen?")}</DialogTitle>
            <DialogDescription>
              {t("Deze actie kan niet ongedaan worden gemaakt. De scenario's herrekenen automatisch zonder deze lening.")}
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm">
            <span className="font-bold">{confirmDelete?.name}</span>
          </div>
          <DialogFooter>
            <button onClick={() => setConfirmDelete(null)} className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold text-sm">
              {t("Annuleren")}
            </button>
            <button
              onClick={() => confirmDelete && doDelete(confirmDelete.id)}
              className="btn-brutal bg-destructive text-destructive-foreground px-4 py-2 font-display uppercase font-bold text-sm"
              data-testid="exloan-confirm-delete"
            >
              {t("Verwijderen")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
